// The expanded view of one family: children, lifetime value with its workings, timeline, identities and notes.

import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CakeIcon from "@mui/icons-material/Cake";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import EventIcon from "@mui/icons-material/Event";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import SchoolIcon from "@mui/icons-material/School";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import FlagIcon from "@mui/icons-material/Flag";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import BlockIcon from "@mui/icons-material/Block";
import type { CrmChild, CrmCustomer, Identity, Stage, TimelineEvent, TimelineKind } from "../../shared/crm";
import { STAGE_LABEL, STAGES } from "../../shared/crm";
import type { CrmActions, Planned } from "./actions";
import { ServiceBadge, STAGE_COLOR, STAGE_HINT, Section, ageText, money, monthDate, shortDate } from "./ui";

const KIND_ICON: Record<TimelineKind, React.ReactElement> = {
  enquiry: <ChatBubbleOutlineIcon fontSize="small" />,
  registration: <HowToRegIcon fontSize="small" />,
  start: <EventIcon fontSize="small" />,
  booking: <BeachAccessIcon fontSize="small" />,
  cancelledBooking: <BlockIcon fontSize="small" />,
  invoice: <ReceiptLongIcon fontSize="small" />,
  payment: <PaymentsIcon fontSize="small" />,
  note: <StickyNote2Icon fontSize="small" />,
  stage: <FlagIcon fontSize="small" />,
  school: <SchoolIcon fontSize="small" />,
  left: <EventIcon fontSize="small" />,
};

const ChildRow: React.FC<{
  child: CrmChild;
  customer: CrmCustomer;
  today: string;
  identities: Map<string, Identity>;
  onDob: (child: CrmChild) => void;
  onMove: (identityKey: string, target: CrmChild | null) => void;
}> = ({ child, customer, today, identities, onDob, onMove }) => {
  const [anchor, setAnchor] = useState<{ el: HTMLElement; key: string } | null>(null);
  const age = ageText(child.dateOfBirth, today);
  const realised = child.preschool.invoicedPence + child.holiday.invoicedPence;
  const predicted = child.prediction.preschoolPence + child.prediction.holidayPence;
  return (
    <Box sx={{ py: 1.25 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography fontWeight={600}>{child.name}</Typography>
        <Chip size="small" variant="outlined" color={STAGE_COLOR[child.stage]} label={STAGE_LABEL[child.stage]} />
        {child.services.map((s) => <ServiceBadge key={s} service={s} />)}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {money(realised)} <Typography component="span" variant="body2" color="text.secondary">+ {money(predicted)} predicted</Typography>
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
        <Tooltip title={child.dateOfBirth ? `Born ${shortDate(child.dateOfBirth)} (${child.dobSource})` : "Add the date of birth to forecast by age"}>
          <Chip size="small" icon={<CakeIcon />} variant={child.dateOfBirth ? "filled" : "outlined"} color={child.dateOfBirth ? "default" : "warning"}
            label={age ?? "Add date of birth"} onClick={() => onDob(child)} />
        </Tooltip>
        <Typography variant="body2" color="text.secondary">
          {child.stageReason}
          {child.schoolStart ? ` · school ${monthDate(child.schoolStart)}` : ""}
          {child.holidayBookings ? ` · ${child.holidayBookings} holiday booking${child.holidayBookings === 1 ? "" : "s"}` : ""}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
        {child.identityKeys.map((key) => {
          const identity = identities.get(key);
          return (
            <Chip key={key} size="small" variant="outlined"
              label={`${identity?.childName ?? key} · ${identity?.label ?? ""}`}
              onDelete={(e) => setAnchor({ el: e.currentTarget as HTMLElement, key })}
              deleteIcon={<MoreHorizIcon titleAccess="Same child as another, or a different child?" />} />
          );
        })}
      </Stack>
      <Menu anchorEl={anchor?.el} open={!!anchor} onClose={() => setAnchor(null)}>
        {customer.children.filter((c) => c.key !== child.key).map((other) => (
          <MenuItem key={other.key} onClick={() => { onMove(anchor!.key, other); setAnchor(null); }}>
            <ListItemText primary={`Same child as ${other.name}`} />
          </MenuItem>
        ))}
        <MenuItem disabled={child.identityKeys.length < 2} onClick={() => { onMove(anchor!.key, null); setAnchor(null); }}>
          <ListItemText primary="A different child" secondary="Make this a child of its own" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

const TimelineItem: React.FC<{ event: TimelineEvent }> = ({ event }) => {
  const theme = useTheme();
  const color = event.service === "holiday" ? theme.palette.warning.main : event.service === "preschool" ? theme.palette.primary.main : theme.palette.text.secondary;
  return (
    <Stack direction="row" spacing={1.5} sx={{ py: 0.75, opacity: event.future ? 0.75 : 1 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center",
        color, bgcolor: alpha(color, 0.12), border: event.future ? `1px dashed ${color}` : "none",
      }}>
        {KIND_ICON[event.kind]}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{event.title}{event.future ? " (upcoming)" : ""}</Typography>
          {event.amountPence !== null && <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(event.amountPence)}</Typography>}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "pre-wrap" }}>
          {shortDate(event.at)}{event.detail ? ` · ${event.detail}` : ""}
        </Typography>
      </Box>
    </Stack>
  );
};

export const CustomerDetail: React.FC<{
  customer: CrmCustomer;
  today: string;
  identities: Map<string, Identity>;
  actions: CrmActions;
  run: (planned: Planned, message: string) => void;
}> = ({ customer, today, identities, actions, run }) => {
  const [note, setNote] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [dobFor, setDobFor] = useState<CrmChild | null>(null);
  const [dob, setDob] = useState("");
  const [stageOpen, setStageOpen] = useState(false);
  const [stage, setStage] = useState<Stage | "auto">("auto");
  const [stageNote, setStageNote] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);

  const r = customer.realised;
  const paidShare = r.invoicedPence ? (r.paidPence / r.invoicedPence) * 100 : 0;
  const predicted = customer.prediction.preschoolPence + customer.prediction.holidayPence;
  const timeline = showAll ? customer.timeline : customer.timeline.slice(0, 8);
  const splittable = customer.identities.length > 1;

  return (
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" }, p: { xs: 1.5, md: 2 } }}>
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <Section title={`Children (${customer.children.length})`}>
          {customer.children.length === 0 && <Typography variant="body2" color="text.secondary">No children on record.</Typography>}
          {customer.children.map((child, i) => (
            <React.Fragment key={child.key}>
              {i > 0 && <Divider />}
              <ChildRow child={child} customer={customer} today={today} identities={identities}
                onDob={(c) => { setDob(c.dateOfBirth ?? ""); setDobFor(c); }}
                onMove={(key, target) => run(actions.moveToChild(customer, key, target),
                  target ? `Linked to ${target.name}` : "Made a separate child")} />
            </React.Fragment>
          ))}
        </Section>

        <Section title="Timeline" action={customer.timeline.length > 8 && (
          <Button size="small" onClick={() => setShowAll(!showAll)}>{showAll ? "Show less" : `Show all ${customer.timeline.length}`}</Button>
        )}>
          {timeline.map((event, i) => <TimelineItem key={`${event.at}-${event.title}-${i}`} event={event} />)}
          {customer.timeline.length === 0 && <Typography variant="body2" color="text.secondary">Nothing yet.</Typography>}
        </Section>
      </Stack>

      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <Section title="Lifetime value">
          <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Realised</Typography>
              <Typography variant="h6" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(r.invoicedPence)}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Predicted future</Typography>
              <Typography variant="h6" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(predicted)}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Lifetime</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>{money(customer.ltvPence)}</Typography>
            </Box>
          </Stack>
          <LinearProgress variant="determinate" color="success" value={paidShare} sx={{ height: 6, borderRadius: 3 }} />
          <Typography variant="caption" color="text.secondary">
            {money(r.paidPence)} paid{r.invoicedPence > r.paidPence ? ` · ${money(r.invoicedPence - r.paidPence)} outstanding` : ""}
            {" · "}preschool {money(r.preschoolPence)} · holiday {money(r.holidayPence)}
          </Typography>
          {customer.prediction.lines.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>How the forecast is worked out</Typography>
              {customer.prediction.lines.map((line) => (
                <Box key={line.label} sx={{ mt: 0.75 }}>
                  <Stack direction="row"><Typography variant="body2" sx={{ flex: 1 }}>{line.label}</Typography>
                    <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(line.pence)}</Typography></Stack>
                  <Typography variant="caption" color="text.secondary">{line.detail}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Section>

        <Section title="Stage" action={<Button size="small" startIcon={<EditIcon />} onClick={() => {
          setStage(customer.manualStage ? customer.stage : "auto");
          setStageNote(customer.manualStage ? customer.stageReason : "");
          setStageOpen(true);
        }}>Change</Button>}>
          <Typography variant="body2">
            <b>{STAGE_LABEL[customer.stage]}</b>{customer.manualStage ? ` (set by hand; data says ${STAGE_LABEL[customer.derivedStage]})` : ""}
          </Typography>
          <Typography variant="caption" color="text.secondary">{customer.stageReason}</Typography>
        </Section>

        <Section title="Contact and records" action={
          <Tooltip title="Rename this family"><IconButton size="small" onClick={() => setRenaming(customer.doc?.displayName ?? customer.name)}><EditIcon fontSize="small" /></IconButton></Tooltip>
        }>
          {customer.emails.map((e) => <Typography key={e} variant="body2"><a href={`mailto:${e}`}>{e}</a></Typography>)}
          {customer.phones.map((p) => <Typography key={p} variant="body2"><a href={`tel:${p.replace(/\s/g, "")}`}>{p}</a></Typography>)}
          {customer.addresses.map((x) => <Typography key={x} variant="body2" color="text.secondary">{x}</Typography>)}
          <Divider sx={{ my: 1 }} />
          {customer.identities.map((identity) => (
            <Stack key={identity.key} direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>{identity.label}{identity.childName ? ` · ${identity.childName}` : ""}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {[identity.parentNames[0], identity.emails[0], identity.at && shortDate(identity.at)].filter(Boolean).join(" · ")}
                </Typography>
              </Box>
              {splittable && (
                <Tooltip title="Not this family: split it off (remembered)">
                  <IconButton size="small" onClick={() => run(actions.split(customer, identity.key), `${identity.label} split off`)}>
                    <CallSplitIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          ))}
        </Section>

        <Section title="Notes">
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <TextField size="small" fullWidth multiline maxRows={4} placeholder="Add a note" value={note} onChange={(e) => setNote(e.target.value)} />
            <Button variant="contained" size="small" disabled={!note.trim()} onClick={() => {
              run(actions.addNote(customer, note.trim()), "Note added");
              setNote("");
            }}>Add</Button>
          </Stack>
          {customer.notes.map((n) => (
            <Stack key={n.id} direction="row" spacing={1} sx={{ py: 0.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{n.text}</Typography>
                <Typography variant="caption" color="text.secondary">{shortDate(n.at)}</Typography>
              </Box>
              <IconButton size="small" onClick={() => run(actions.deleteNote(customer, n.id), "Note deleted")}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Stack>
          ))}
        </Section>
      </Stack>

      <Dialog open={!!dobFor} onClose={() => setDobFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>Date of birth · {dobFor?.name}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {dobFor?.dobSource === "registration"
              ? "From the preschool registration. A date here overrides it for the CRM only."
              : "Holiday Club forms do not ask for it. With a date the forecast uses the child's real age."}
          </DialogContentText>
          <TextField type="date" fullWidth label="Date of birth" InputLabelProps={{ shrink: true }} value={dob} onChange={(e) => setDob(e.target.value)} />
        </DialogContent>
        <DialogActions>
          {dobFor?.dobSource === "manual" && <Button color="error" onClick={() => { run(actions.setChildDob(customer, dobFor!, null), "Date of birth removed"); setDobFor(null); }}>Remove</Button>}
          <Button onClick={() => setDobFor(null)}>Cancel</Button>
          <Button variant="contained" disabled={!/^\d{4}-\d{2}-\d{2}$/.test(dob)} onClick={() => {
            run(actions.setChildDob(customer, dobFor!, dob), `Date of birth saved for ${dobFor!.name}`);
            setDobFor(null);
          }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={stageOpen} onClose={() => setStageOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Stage · {customer.name}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Automatic follows the data (now: {STAGE_LABEL[customer.derivedStage]}). A stage set by hand stays until you switch back,
            and also drives the forecast (e.g. Alumni = no future value).
          </DialogContentText>
          <ToggleButtonGroup exclusive size="small" value={stage} onChange={(_, v) => v && setStage(v)} sx={{ flexWrap: "wrap", mb: 2 }}>
            <ToggleButton value="auto">Automatic</ToggleButton>
            {STAGES.map((s) => <ToggleButton key={s} value={s}>{STAGE_LABEL[s]}</ToggleButton>)}
          </ToggleButtonGroup>
          {stage !== "auto" && (
            <>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>{STAGE_HINT[stage]}</Typography>
              <TextField fullWidth size="small" label="Why (optional)" placeholder="e.g. Moved away" value={stageNote} onChange={(e) => setStageNote(e.target.value)} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStageOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            run(actions.setStage(customer, stage === "auto" ? null : stage, stageNote.trim()),
              stage === "auto" ? "Stage back to automatic" : `Stage set to ${STAGE_LABEL[stage]}`);
            setStageOpen(false);
          }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renaming !== null} onClose={() => setRenaming(null)} fullWidth maxWidth="xs">
        <DialogTitle>Family name</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth sx={{ mt: 1 }} label="Shown in the list" value={renaming ?? ""} onChange={(e) => setRenaming(e.target.value)}
            helperText="Leave empty to use the parent's name from the forms" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => { run(actions.rename(customer, renaming ?? ""), "Name saved"); setRenaming(null); }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
